import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import DigitalTwin from "@/components/DigitalTwin";
import SavedTwins from "@/components/SavedTwins";
import api from "@/lib/api";
import { toast } from "sonner";
import { Hourglass, Activity, ArrowRight, Info, Check, Leaf } from "lucide-react";

const DIMENSIONS = [
  { key: "energy", label: "Energy", hint: "Daytime vitality & stamina" },
  { key: "sleep", label: "Sleep", hint: "Depth & restfulness" },
  { key: "mood", label: "Mood", hint: "Emotional steadiness" },
  { key: "digestion", label: "Digestion", hint: "Gut comfort & regularity" },
  { key: "recovery", label: "Recovery", hint: "Bounce-back after exertion" },
];

const HABIT_FIELDS = [
  { key: "exercise", label: "Exercise" }, { key: "diet", label: "Diet" },
  { key: "smoking", label: "Smoking" }, { key: "alcohol", label: "Alcohol" },
  { key: "stress", label: "Stress" }, { key: "sleep_hours", label: "Sleep hours" },
];
const OPT_LABEL = { lt5: "< 5 hrs", "5-6": "5–6 hrs", "7-8": "7–8 hrs", gt8: "> 8 hrs" };
const optLabel = (v) => OPT_LABEL[v] || (v ? v.charAt(0).toUpperCase() + v.slice(1) : "Not set");

const gapCopy = (gap) => {
  if (gap === null || gap === undefined) return "";
  if (gap <= -1) return `Your body reads about ${Math.abs(gap)} ${Math.abs(gap) === 1 ? "year" : "years"} younger than the calendar.`;
  if (gap >= 1) return `Your body currently reads about ${gap} ${gap === 1 ? "year" : "years"} older than the calendar — and this is modifiable.`;
  return "Your biological and chronological age are closely aligned.";
};

const gapTone = (gap) => (gap === null ? "neutral" : gap <= -1 ? "good" : gap >= 3 ? "attention" : "neutral");

export default function BioAge() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [scores, setScores] = useState({ energy: 3, sleep: 3, mood: 3, digestion: 3, recovery: 3 });
  const [habits, setHabits] = useState({});
  const [twins, setTwins] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadTwins = () => {
    api.get("/bioage/twins").then((r) => setTwins(r.data || [])).catch(() => {});
  };

  const load = () => {
    setLoading(true);
    setError(false);
    api.get("/bioage")
      .then((r) => {
        setData(r.data);
        if (r.data?.symptom_checkin) {
          const c = r.data.symptom_checkin;
          setScores({
            energy: c.energy ?? 3, sleep: c.sleep ?? 3, mood: c.mood ?? 3,
            digestion: c.digestion ?? 3, recovery: c.recovery ?? 3,
          });
        }
        const savedHabits = r.data?.lifestyle?.habits || {};
        setHabits(HABIT_FIELDS.reduce((a, h) => ({ ...a, [h.key]: savedHabits[h.key] || "" }), {}));
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);
  useEffect(loadTwins, []);

  const habitOptions = data?.habit_options || {};

  const submitCheckin = async () => {
    setSaving(true);
    try {
      const cleanHabits = Object.fromEntries(Object.entries(habits).filter(([, v]) => v));
      const { data: fresh } = await api.post("/symptom-checkin", { ...scores, ...cleanHabits });
      setData(fresh);
      toast.success("Weekly check-in saved.");
    } catch {
      toast.error("Could not save your check-in. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const bio = data?.biological_age;
  const chrono = data?.chronological_age;
  const gap = data?.age_gap;
  const tone = gapTone(gap);
  const toneColor = tone === "good" ? "#3F6B4C" : tone === "attention" ? "#C05C42" : "#8a6620";

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <Header />
      <section className="px-6 sm:px-12 lg:px-20 pt-16 pb-8 max-w-5xl mx-auto">
        <p className="label-eyebrow mb-3">Vitality mirror</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2D322B]" data-testid="bioage-title">
          Biological Age
        </h1>
        <p className="mt-4 text-[#5C6359] max-w-2xl">
          A gentle, science-grounded estimate of how your body is ageing — drawn from your own biomarkers.
          Not a verdict, but a starting point you can move.
        </p>
      </section>

      <section className="px-6 sm:px-12 lg:px-20 pb-24 max-w-5xl mx-auto space-y-8">
        {loading ? (
          <div className="text-[#5C6359]" data-testid="bioage-loading">Reading your trail…</div>
        ) : error ? (
          <div className="bg-[#2D322B] text-[#F9F6F0] rounded-3xl p-10" data-testid="bioage-error">
            <Info className="w-7 h-7 text-[#C05C42] mb-4" />
            <p className="font-serif text-2xl mb-3">We couldn&apos;t load your Bio-Age just now.</p>
            <p className="text-[#F9F6F0]/80 max-w-xl">There was a problem reaching the estimate service. Please try again in a moment.</p>
            <button onClick={load} className="btn-primary !bg-[#D49A44] !text-[#2D322B] hover:!bg-[#bf8a3a] mt-6 inline-flex items-center gap-2" data-testid="bioage-retry">
              Try again <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ) : !chrono ? (
          <div className="bg-[#2D322B] text-[#F9F6F0] rounded-3xl p-10" data-testid="bioage-needs-intake">
            <Hourglass className="w-7 h-7 text-[#D49A44] mb-4" />
            <p className="font-serif text-2xl mb-3">We need your age first.</p>
            <p className="text-[#F9F6F0]/80 max-w-xl">Complete your intake so we can anchor your estimate to your chronological age.</p>
            <Link to="/intake" className="btn-primary !bg-[#D49A44] !text-[#2D322B] hover:!bg-[#bf8a3a] mt-6 inline-flex items-center gap-2" data-testid="bioage-intake-cta">
              Begin intake <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <>
            {/* Result hero */}
            <div className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl p-8 sm:p-10" data-testid="bioage-result">
              {data.provisional && data.estimate_source === "lifestyle" && bio != null ? (
                <div data-testid="bioage-lifestyle">
                  <p className="label-eyebrow !text-[#8a6620]">Provisional estimate — refine with labs</p>
                  <div className="mt-4 grid sm:grid-cols-3 gap-6 items-center">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-[#5C6359]">Chronological</p>
                      <p className="font-serif text-5xl text-[#2D322B] mt-1" data-testid="bioage-chrono">{chrono}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-[#5C6359]">Biological (est.)</p>
                      <p className="font-serif text-6xl mt-1" style={{ color: toneColor }} data-testid="bioage-bio">{bio}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-[#5C6359]">Gap</p>
                      <p className="font-serif text-5xl mt-1" style={{ color: toneColor }} data-testid="bioage-gap">
                        {gap > 0 ? `+${gap}` : gap}
                      </p>
                    </div>
                  </div>
                  <p className="mt-6 text-[#2D322B] max-w-2xl leading-relaxed" data-testid="bioage-gap-copy">{gapCopy(gap)}</p>
                  <p className="mt-2 text-sm text-[#5C6359] max-w-2xl leading-relaxed">
                    This is a lifestyle-based estimate drawn from your intake and weekly check-in — a starting point.
                    Upload a lab panel to replace it with a precise PhenoAge reading from your bloodwork.
                  </p>
                  <Link to="/biomarkers" className="btn-primary mt-6 inline-flex items-center gap-2" data-testid="bioage-upload-labs-cta">
                    Add your labs <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ) : data.provisional ? (
                <div data-testid="bioage-provisional">
                  <p className="label-eyebrow !text-[#8a6620]">Provisional — awaiting labs</p>
                  <p className="font-serif text-2xl text-[#2D322B] mt-2 max-w-2xl leading-snug">
                    Upload a lab panel to unlock your biological-age estimate. In the meantime, here&apos;s your
                    self-reported wellness.
                  </p>
                  <div className="mt-6 flex items-end gap-4">
                    {data.symptom_score != null ? (
                      <div>
                        <p className="font-serif text-6xl text-[#2D322B]" data-testid="bioage-wellness-score">
                          {data.symptom_score}
                        </p>
                        <p className="text-xs uppercase tracking-widest text-[#5C6359] mt-1">Wellness score / 100</p>
                      </div>
                    ) : (
                      <p className="text-sm text-[#5C6359]" data-testid="bioage-wellness-hint">
                        Complete this week&apos;s check-in below to see your wellness score.
                      </p>
                    )}
                  </div>
                  <Link to="/biomarkers" className="btn-primary mt-6 inline-flex items-center gap-2" data-testid="bioage-upload-labs-cta">
                    Add your labs <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ) : (
                <div>
                  <p className="label-eyebrow">Your estimate</p>
                  <div className="mt-4 grid sm:grid-cols-3 gap-6 items-center">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-[#5C6359]">Chronological</p>
                      <p className="font-serif text-5xl text-[#2D322B] mt-1" data-testid="bioage-chrono">{chrono}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-[#5C6359]">Biological</p>
                      <p className="font-serif text-6xl mt-1" style={{ color: toneColor }} data-testid="bioage-bio">{bio}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-[#5C6359]">Gap</p>
                      <p className="font-serif text-5xl mt-1" style={{ color: toneColor }} data-testid="bioage-gap">
                        {gap > 0 ? `+${gap}` : gap}
                      </p>
                    </div>
                  </div>
                  <p className="mt-6 text-[#2D322B] max-w-2xl leading-relaxed" data-testid="bioage-gap-copy">{gapCopy(gap)}</p>
                </div>
              )}
              <div className="mt-8 flex items-start gap-3 rounded-2xl bg-[#F9F6F0] border border-[#D1C7B8] p-4" data-testid="bioage-disclaimer">
                <Info className="w-4 h-4 text-[#8a6620] shrink-0 mt-0.5" />
                <p className="text-xs text-[#5C6359] leading-relaxed">{data.disclaimer}</p>
              </div>
            </div>

            {/* Markers used / missing */}
            {!data.provisional && (
              <div className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl p-8" data-testid="bioage-markers">
                <div className="flex items-center gap-3 mb-5">
                  <Activity className="w-5 h-5 text-[#C05C42]" />
                  <p className="label-eyebrow">What shaped this number</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {data.markers_used.map((m) => (
                    <div key={m.key} className="flex items-center justify-between rounded-xl bg-[#F9F6F0] border border-[#D1C7B8] px-4 py-3" data-testid={`bioage-marker-${m.key}`}>
                      <span className="text-sm text-[#2D322B]">{m.label}</span>
                      <span className="text-sm font-medium text-[#2D322B]">{m.value} <span className="text-[#5C6359] text-xs">{m.unit}</span></span>
                    </div>
                  ))}
                </div>
                {data.missing_markers?.length > 0 && (
                  <p className="mt-5 text-xs text-[#5C6359] leading-relaxed" data-testid="bioage-missing">
                    Using population-average estimates for: <span className="text-[#2D322B]">{data.missing_markers.join(", ")}</span>.
                    Add these to your next lab upload to sharpen your estimate.
                  </p>
                )}
              </div>
            )}

            {/* Lifestyle factors (provisional estimate) */}
            {data.provisional && data.estimate_source === "lifestyle" && (
              <div className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl p-8" data-testid="bioage-lifestyle-factors">
                <div className="flex items-center gap-3 mb-5">
                  <Activity className="w-5 h-5 text-[#C05C42]" />
                  <p className="label-eyebrow">What shaped this estimate</p>
                </div>
                {data.estimate_factors?.length > 0 ? (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {data.estimate_factors.map((f, i) => (
                      <div key={i} className="flex items-center justify-between rounded-xl bg-[#F9F6F0] border border-[#D1C7B8] px-4 py-3" data-testid={`bioage-factor-${i}`}>
                        <span className="text-sm text-[#2D322B]">{f.label}</span>
                        <span className="text-sm font-medium" style={{ color: f.effect > 0 ? "#C05C42" : "#3F6B4C" }}>
                          {f.effect > 0 ? `+${f.effect}` : f.effect} {Math.abs(f.effect) === 1 ? "yr" : "yrs"}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[#5C6359] leading-relaxed">
                    Anchored to your chronological age — no ageing signals from your intake or check-in yet.
                    A lab panel will give you a precise, biomarker-based reading.
                  </p>
                )}
                <p className="mt-5 text-xs text-[#5C6359] leading-relaxed">
                  A lightweight estimate from self-reported inputs. Your bloodwork replaces this with the clinical PhenoAge model.
                </p>
              </div>
            )}

            {/* Modifiability / protocols */}
            <div className="bg-[#2D322B] text-[#F9F6F0] rounded-3xl p-8 sm:p-10" data-testid="bioage-modifiable">
              <Leaf className="w-6 h-6 text-[#D49A44] mb-4" />
              <p className="label-eyebrow !text-[#D49A44]">This is modifiable</p>
              <p className="font-serif text-2xl mt-2 max-w-2xl leading-snug">
                Biological age responds to how you live. Your protocols are built to move it.
              </p>
              {data.improvement_potential && (
                <div className="mt-6 rounded-2xl bg-[#F9F6F0]/10 border border-[#D49A44]/40 p-5 max-w-2xl" data-testid="bioage-nudge">
                  <p className="text-[#F9F6F0] leading-relaxed">
                    Following{data.top_protocol ? <> your <span className="text-[#E4B564] font-medium">{data.top_protocol}</span></> : " your protocols"} could
                    shave roughly <span className="font-serif text-2xl text-[#E4B564]" data-testid="bioage-nudge-years">{data.improvement_potential}</span> {data.improvement_potential === 1 ? "year" : "years"} off this{data.provisional ? " estimate" : " number"} over time.
                    {data.provisional ? " Add a lab panel to track it precisely." : " Keep at it and re-test to watch it shift."}
                  </p>
                </div>
              )}
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/dashboard" className="btn-primary !bg-[#D49A44] !text-[#2D322B] hover:!bg-[#bf8a3a] inline-flex items-center gap-2" data-testid="bioage-protocols-cta">
                  View my protocols <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/concierge" className="inline-flex items-center gap-2 text-[#D49A44] hover:text-[#F9F6F0] text-sm uppercase tracking-widest self-center" data-testid="bioage-massey-cta">
                  Ask Massey <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </>
        )}

        {/* Digital Twin — tier 2 what-if */}
        {chrono && !error && data?.pheno_inputs?.length > 0 && (
          <DigitalTwin data={data} onSaved={loadTwins} />
        )}

        {/* Saved twins + progress */}
        {chrono && !error && (
          <SavedTwins twins={twins} onChanged={loadTwins} />
        )}

        {/* Weekly symptom check-in */}
        {chrono && (
          <div className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl p-8" data-testid="symptom-checkin">
            <p className="label-eyebrow">Weekly check-in</p>
            <p className="font-serif text-2xl text-[#2D322B] mt-2">How are you feeling this week?</p>
            <p className="text-sm text-[#5C6359] mt-2 max-w-xl">
              A quick 1–5 read on how your body feels. This lives alongside your estimate — it grounds the numbers in lived experience.
            </p>
            <div className="mt-6 space-y-6">
              {DIMENSIONS.map((d) => (
                <div key={d.key} data-testid={`symptom-${d.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-sm font-medium text-[#2D322B]">{d.label}</span>
                      <span className="text-xs text-[#5C6359] ml-2">{d.hint}</span>
                    </div>
                    <span className="text-sm font-semibold text-[#C05C42]" data-testid={`symptom-${d.key}-value`}>{scores[d.key]}</span>
                  </div>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setScores((s) => ({ ...s, [d.key]: n }))}
                        data-testid={`symptom-${d.key}-${n}`}
                        className={`flex-1 h-10 rounded-lg border text-sm transition-colors ${
                          scores[d.key] === n
                            ? "bg-[#C05C42] text-[#F9F6F0] border-[#C05C42]"
                            : "bg-[#F9F6F0] text-[#5C6359] border-[#D1C7B8] hover:border-[#C05C42]"
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {Object.keys(habitOptions).length > 0 && (
              <div className="mt-8" data-testid="checkin-habits">
                <p className="label-eyebrow mb-1">Lifestyle habits</p>
                <p className="text-xs text-[#5C6359] mb-4 max-w-xl">Saved with your check-in — these power your Lifestyle-adjusted Bio-Age & Digital Twin.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {HABIT_FIELDS.filter((h) => habitOptions[h.key]).map((h) => (
                    <div key={h.key} className="flex items-center justify-between gap-4" data-testid={`checkin-habit-${h.key}`}>
                      <label className="text-sm text-[#2D322B]">{h.label}</label>
                      <select
                        value={habits[h.key] || ""}
                        onChange={(e) => setHabits((s) => ({ ...s, [h.key]: e.target.value }))}
                        data-testid={`checkin-habit-${h.key}-select`}
                        className="bg-[#F9F6F0] border border-[#D1C7B8] rounded-lg px-3 py-1.5 text-sm text-[#2D322B] focus:border-[#C05C42] outline-none min-w-[140px]"
                      >
                        <option value="">Not set</option>
                        {habitOptions[h.key].map((opt) => <option key={opt} value={opt}>{optLabel(opt)}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={submitCheckin}
              disabled={saving}
              data-testid="symptom-submit"
              className="btn-primary mt-8 inline-flex items-center gap-2 disabled:opacity-60"
            >
              {saving ? "Saving…" : <>Save this week <Check className="w-4 h-4" /></>}
            </button>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
